import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useTableSelector } from 'hooks/useTableSelector';
import { removeWaitlistEntry } from 'reducers/waitlist';
import { selectIsQueuedFor } from 'reducers/waitlist.selectors';
import { leaveWaitlist } from 'services/waitlistService';
import { isWaitlistGameType } from 'types/waitlist';

/**
 * Remove the player from the waitlist once the engine seats them at a table
 * they were queued for.
 *
 * The engine only dequeues a waitlisted player on EXPIRY (max bumps —
 * apps/engine/games/common/waitlist.js processWaitlist), never on a successful
 * sit. So, exactly like Unity — which calls RemoveFromWaitlistByPlayer in
 * JoinGame's onSuccess callback (BuyInView.cs:688) — the client must delete the
 * waitlist entry itself after joining.
 *
 * sitDown is a fire-and-forget emit with no ack (GameService.tsx — a plain
 * emit since 8/25; it was volatile, which dropped joins under socket churn),
 * so we key off the engine-confirmed seated broadcast (currentPlayer.isSeated,
 * set from gotTexas/gotOmaha in reducers/currentTable). This covers every seat
 * path — Join Table, Accept Seat (manual buy-in), and Accept Seat (auto
 * buy-in) — in one place.
 */
export const useDequeueOnSeated = () => {
  const dispatch = useAppDispatch();
  const isSeated = useTableSelector((table) => table.currentPlayer.isSeated);
  const gameIdRaw = useTableSelector((table) => table.game.gameID);
  const gameTypeField = useTableSelector((table) => table.game.gameType);
  const gameVariant = useTableSelector((table) => table.game.game);
  const playerGuid = useAppSelector((state) => state.user.playerGUID) || '';
  const numericGameId = Number(gameIdRaw);
  const isQueued = useAppSelector(selectIsQueuedFor(numericGameId));
  // Engine GameData.GameType drives the REST gameType; fall back through the
  // variant enum (matches JoinButton / BuyInModal).
  const gameType = isWaitlistGameType(gameTypeField)
    ? gameTypeField
    : isWaitlistGameType(gameVariant)
    ? gameVariant
    : null;

  // Latch so the DELETE fires once per seating, not on every broadcast while
  // seated. Re-arms when the player is no longer seated (stood up / left).
  const dequeuedRef = useRef(false);
  // Only dequeue on an ACTUAL sit — i.e. the player was seen genuinely unseated
  // at this loaded table (observing / in line) and then became seated. Opening a
  // table the player already holds a seat at loads straight into isSeated=true
  // (seat + game data arrive in the same gotTexas/gotOmaha update —
  // reducers/currentTable.tsx:521), so this stays false and we never delete a
  // waitlist entry they are deliberately keeping. Without it, merely opening
  // such a table (e.g. from the lobby) silently un-queued the player (EN-9527).
  const sawUnseatedAtTableRef = useRef(false);

  useEffect(() => {
    if (!isSeated) {
      // Re-arm; and once the table itself has loaded, record that the player was
      // genuinely unseated here so a later sit counts as a real transition.
      if (numericGameId > 0) sawUnseatedAtTableRef.current = true;
      dequeuedRef.current = false;
      return;
    }
    // isSeated === true. Skip the "already seated on open" case — never a sit.
    if (!sawUnseatedAtTableRef.current) return;
    if (dequeuedRef.current || !isQueued || !gameType || !numericGameId || !playerGuid) return;
    dequeuedRef.current = true;
    dispatch(removeWaitlistEntry({ gameId: numericGameId, playerGuid, myGuid: playerGuid }));
    leaveWaitlist({ gameId: numericGameId, gameType, playerGuid }).catch((err) =>
      console.error('[waitlist] leaveWaitlist on seated failed', err)
    );
  }, [isSeated, isQueued, gameType, numericGameId, playerGuid, dispatch]);
};

export default useDequeueOnSeated;
